export type DateFormatOption = 'locale' | 'dd.MM.yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd' | 'dd/MM/yyyy';

function _applyFormat(d: Date, fmt: DateFormatOption): string {
  if (fmt === 'locale') {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  }
  const dd   = String(d.getDate()).padStart(2, '0');
  const MM   = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return fmt.replace('dd', dd).replace('MM', MM).replace('yyyy', yyyy);
}

const _today = new Date();

function _makeLabel(fmt: DateFormatOption): string {
  if (fmt === 'locale') return `Browser default (${_applyFormat(_today, 'locale')})`;
  return `${fmt} (${_applyFormat(_today, fmt)})`;
}

export const DATE_FORMAT_OPTIONS: { value: DateFormatOption; label: string }[] = [
  { value: 'locale',     label: _makeLabel('locale') },
  { value: 'dd.MM.yyyy', label: _makeLabel('dd.MM.yyyy') },
  { value: 'MM/dd/yyyy', label: _makeLabel('MM/dd/yyyy') },
  { value: 'yyyy-MM-dd', label: _makeLabel('yyyy-MM-dd') },
  { value: 'dd/MM/yyyy', label: _makeLabel('dd/MM/yyyy') },
];

export const DATE_FORMAT_KEY = 'radish-date-format';

let _format: DateFormatOption =
  (localStorage.getItem(DATE_FORMAT_KEY) as DateFormatOption | null) ?? 'locale';

export function getDateFormat(): DateFormatOption {
  return _format;
}

export function setDateFormat(fmt: DateFormatOption): void {
  _format = fmt;
  localStorage.setItem(DATE_FORMAT_KEY, fmt);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value as string);
  if (isNaN(d.getTime())) return String(value);
  return _applyFormat(d, _format);
}

// Same date formatting as formatDate, but with the time-of-day appended — used wherever the
// exact moment (not just the day) matters, e.g. import protocol start/end timestamps.
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value as string);
  if (isNaN(d.getTime())) return String(value);
  const timePart = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(d);
  return `${_applyFormat(d, _format)} ${timePart}`;
}
